/**
 * A **Rule arm**: one diagnostic a rule can report, declared once, so `check`
 * reports through it and `armEmits` derives `emits` from it. A rule therefore
 * cannot report a name or a severity its `emits` omits. The ESLint rule-arm guard
 * refuses a diagnostic built any other way.
 */
import { severityFixedBy, } from './types.js';
/**
 * The arm for `ruleName`, at the severity its grounding fixes. Only an `engine`
 * arm writes its severity, because only its cited line can decide it.
 */
export function groundedArm(ruleName, grounding) {
    return { severity: severityFixedBy(grounding), ruleName, grounding };
}
/** The `emits` list these arms declare: every arm this instance has. */
export function armEmits(arms) {
    return Object.values(arms).filter((arm) => arm !== undefined);
}
/**
 * The stand-in subject for a heading that is no node, such as `[connection]` or `[editable]`: a diagnostic
 * names a subject, and such a heading belongs to none.
 */
export function headingSubject(type) {
    return { name: `<${type}>`, type };
}
/**
 * `arm`'s diagnostic for `node`, the one conversion from arm to report. Fields
 * are named, not spread, or the compiler lets `grounding` ship to every caller.
 * A rule passes no `location`: it reaches its subject through the tree, which
 * carries no lines, and `Linter` puts the report on the node's heading.
 */
export function armDiagnostic(arm, node, message, location) {
    return {
        severity: arm.severity,
        message,
        nodeName: node.name,
        // A heading stating no type has none to report, and an empty string reads
        // in the CLI as a node with an unnameable type rather than an unknown one.
        nodeType: node.type || '<unknown>',
        ruleName: arm.ruleName,
        ...(location ? { location } : {}),
    };
}
/**
 * Append `arm`'s diagnostic for `node`, or nothing when this instance has no
 * such arm: it never declared that name, so it must not report it.
 */
export function reportArm(into, arm, node, message) {
    if (!arm)
        return;
    into.push(armDiagnostic(arm, node, message));
}
//# sourceMappingURL=ruleArms.js.map